/**
 * RefinePanel — the literature facet + year rail (86d3cefah S6, reshaped for 86d3qa4fq).
 *
 * Lifted verbatim out of ResearchDashboardPage's literature tab when the tabs were removed;
 * the behaviour is unchanged. Presentational — every value and handler comes from the page,
 * which still owns the search params.
 *
 * Facet buckets come from the SAME search call as the results (never one request per field —
 * the gateway's per-key rate budget matters) and are capped ~100/field upstream, so this shows
 * the top values for the result set, not an exhaustive enumeration. Hence "Top values in this
 * result set" rather than a promise of completeness.
 *
 * Year filters on `pub_year` (integer, always populated), never `pub_date` (null on ~2%).
 * All colour via --mg-* tokens.
 */

import React from 'react';
import { Typography, Collapse, InputNumber, Button } from 'antd';

import { SectionCard } from '../../components/shared';
import type { FacetBucket, FilterField } from '../../types/research/ResearchTypes';

const { Text } = Typography;

export interface FacetGroup {
	field: FilterField;
	buckets: FacetBucket[];
}

export interface RefinePanelProps {
	facetGroups: FacetGroup[];
	fieldLabel: (f: FilterField) => string;
	onAddTermFilter: (field: FilterField, value: string) => void;
	yearFrom: number | null;
	yearTo: number | null;
	onYearFromChange: (v: number | null) => void;
	onYearToChange: (v: number | null) => void;
	onApplyYear: () => void;
	/** Hidden until there is a query — an empty refine rail is noise, not direction. */
	visible: boolean;
}

const RefinePanel: React.FC<RefinePanelProps> = ({
	facetGroups,
	fieldLabel,
	onAddTermFilter,
	yearFrom,
	yearTo,
	onYearFromChange,
	onYearToChange,
	onApplyYear,
	visible,
}) => {
	if (!visible) return null;

	return (
		<SectionCard
			title="Refine"
			subtitle="Top values in this result set"
			className="mg-research-pane"
		>
			<div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
				{/* Year range */}
				<div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
					<Text className="mg-label">Year</Text>
					<div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
						<InputNumber
							placeholder="From"
							value={yearFrom}
							onChange={onYearFromChange}
							min={1800}
							max={2100}
							controls={false}
							style={{ width: '100%' }}
							data-testid="research-year-from"
						/>
						<Text type="secondary">–</Text>
						<InputNumber
							placeholder="To"
							value={yearTo}
							onChange={onYearToChange}
							min={1800}
							max={2100}
							controls={false}
							style={{ width: '100%' }}
							data-testid="research-year-to"
						/>
					</div>
					<Button size="small" onClick={onApplyYear} data-testid="research-year-apply">
						Apply year
					</Button>
				</div>

				{/* Facets — only fields the gateway actually returned buckets for */}
				{facetGroups.length > 0 && (
					<Collapse
						ghost
						size="small"
						defaultActiveKey={[facetGroups[0].field]}
						data-testid="research-facets"
						items={facetGroups.map((g) => ({
							key: g.field,
							label: (
								<Text style={{ fontSize: 'var(--mg-fs-md)', fontWeight: 600 }}>
									{fieldLabel(g.field)}
								</Text>
							),
							children: (
								<div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
									{g.buckets.map((b) => (
										<div
											key={b.value}
											role="button"
											tabIndex={0}
											onClick={() => onAddTermFilter(g.field, b.value)}
											onKeyDown={(e) => {
												if (e.key === 'Enter' || e.key === ' ') {
													e.preventDefault();
													onAddTermFilter(g.field, b.value);
												}
											}}
											className="mg-research-facet-bucket"
											data-testid={`research-facet-${g.field}-${b.value}`}
											style={{
												display: 'flex',
												justifyContent: 'space-between',
												alignItems: 'baseline',
												gap: 8,
												cursor: 'pointer',
												padding: '2px 4px',
												borderRadius: 'var(--mg-radius-sm)',
											}}
										>
											<Text ellipsis style={{ fontSize: 'var(--mg-fs-md)', flex: 1, minWidth: 0 }}>
												{b.value}
											</Text>
											<Text type="secondary" className="mg-mono-num" style={{ fontSize: 'var(--mg-fs-xs)' }}>
												{b.count.toLocaleString()}
											</Text>
										</div>
									))}
								</div>
							),
						}))}
					/>
				)}
			</div>
		</SectionCard>
	);
};

export default RefinePanel;
